/**
 * GuestList Component
 * Feature: Guests - Real data from Supabase
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useGuests, useDeleteGuest } from "../hooks";
import type { Ospite } from "@/types";

interface GuestListProps {
  onAddGuest?: () => void;
}

export function GuestList({ onAddGuest }: GuestListProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  
  const { data: guests = [], isLoading, refetch } = useGuests(search);
  const deleteGuest = useDeleteGuest();

  const handleAddGuest = () => {
    if (onAddGuest) {
      onAddGuest();
    } else {
      navigate("/ospiti/nuovo");
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/ospiti/${id}`);
  };

  const handleDelete = (id: string) => {
    deleteGuest.mutate(id, {
      onSuccess: () => setConfirmDel(null),
    });
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-32 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="space-y-2">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Ospiti</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{guests.length} ospiti registrati</p>
        </div>
        <button 
          onClick={handleAddGuest} 
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" /> Aggiungi ospite
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input 
          className="w-full border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" 
          placeholder="Cerca per nome, email..." 
          value={search} 
          onChange={e => setSearch(e.target.value)} 
        />
      </div>

      {/* Drawer */}
      <LargeDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedGuestId ? "Modifica Ospite" : "Nuovo Ospite"}
        description={selectedGuestId ? "Modifica i dati dell'ospite" : "Inserisci i dati del nuovo ospite"}
      >
        <GuestForm
          guestId={selectedGuestId || undefined}
          onClose={() => setDrawerOpen(false)}
          onSuccess={() => refetch()}
        />
      </LargeDrawer>

      {/* List */}
      {guests.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Users className="h-10 w-10 text-border mx-auto mb-3" />
          <p className="text-muted-foreground">
            {search ? "Nessun ospite trovato per questa ricerca." : "Nessun ospite registrato."}
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border divide-y divide-border">
          {guests.map((g: Ospite) => (
            <div key={g.id} className="px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
              <div>
                <div className="font-medium text-foreground">{g.nome} {g.cognome}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {[g.email, g.telefono].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex gap-2">
                {confirmDel === g.id ? (
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => handleDelete(g.id)} 
                      disabled={deleteGuest.isPending}
                      className="text-xs px-2 py-1 bg-destructive text-white rounded-lg font-medium disabled:opacity-50"
                    >
                      {deleteGuest.isPending ? "..." : "Elimina"}
                    </button>
                    <button 
                      onClick={() => setConfirmDel(null)} 
                      className="text-xs px-2 py-1 border border-border rounded-lg text-muted-foreground"
                    >
                      Annulla
                    </button>
                  </div>
                ) : (
                  <>
                    <button 
                      onClick={() => handleEdit(g.id)} 
                      className="p-2 rounded-lg hover:bg-slate-100 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => setConfirmDel(g.id)} 
                      className="p-2 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
